import { useTry } from '@ps-smart-cp/ppfw-client';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { CustomFieldBasicSetting, CustomFieldLedgerSetting } from '../features/custom-field/models';
import { fetchBasicSettings, fetchLedgerSettings } from '../features/custom-field/repositories';

interface CustomFieldSettings {
  basicSetting: CustomFieldBasicSetting[];
  ledgerSetting: CustomFieldLedgerSetting[];
}

interface CustomFieldSettingsContextProps extends CustomFieldSettings {
  refetch: () => Promise<void>;
}

const CustomFieldContext = createContext<CustomFieldSettingsContextProps | null>(null);

function CustomFieldSettingsProvider({ children }: { children: ReactNode }) {
  const [customFieldSettings, setCustomFieldSettings] = useState<CustomFieldSettings | null>(null);
  const { tryRequest } = useTry();

  const fetchData = useCallback(
    async () =>
      await tryRequest(async () => {
        const [basicSetting, ledgerSetting] = await Promise.all([
          (await fetchBasicSettings()).map(CustomFieldBasicSetting.fromRaw),
          (await fetchLedgerSettings()).map(CustomFieldLedgerSetting.fromRaw),
        ]);
        setCustomFieldSettings({
          basicSetting: basicSetting.toSorted((a, b) => a.customFieldKey - b.customFieldKey),
          ledgerSetting,
        });
      }),
    [],
  );

  useEffect(() => void fetchData(), [fetchData]);

  return customFieldSettings ? (
    <CustomFieldContext.Provider value={{ ...customFieldSettings, refetch: fetchData }}>
      {children}
    </CustomFieldContext.Provider>
  ) : null;
}

function useCustomFieldSettings() {
  const ctx = useContext(CustomFieldContext);
  if (!ctx) {
    throw new Error('Context does not exist. Error occurred when fetching custom field settings.');
  }

  return ctx;
}

export type { CustomFieldSettingsContextProps };
export { CustomFieldSettingsProvider, useCustomFieldSettings };
